import { useMemo } from 'react';
import { type SessionParticipant } from '../../webrtc/session';
import {
  getSessionParticipantsMap,
  getAllSessionParticipantsMap,
} from '../../features/session/session.selectors';
import { getAllMembersMap } from '../../features/realtime/realtime.selectors';
import {
  type RealtimeMember,
  RealtimeRole,
} from '../../features/realtime/realtime.types';
import { useAppSelector } from '../useReduxToolkit';
import { useTestPatient } from '../useTestPatient';
import { Resolve } from '@doxyme/types';
import { getChatUserIdFromIdentity } from '@doxyme/frontend/features/realtime/realtime.helpers';
import { getCurrentRoomId } from '../../features/apolloSession/apolloSession.selectors';

export type ProviderViewRealtimeCallParticipant = Resolve<
  RealtimeMember & SessionParticipant & { chatUserId: string }
>;

export type ProviderViewSipCallParticipant = Resolve<
  SessionParticipant & {
    identity: { id: string; role: RealtimeRole };
    picture?: undefined;
    roomId?: string;
    isTestPatient: false;
  }
>;

export type ProviderViewCallParticipant =
  | ProviderViewRealtimeCallParticipant
  | ProviderViewSipCallParticipant;

export type UseProviderViewCallParticipantsReturnType = {
  /**
   * Map of current call participants, keyed by participant ID.
   */
  callParticipantsMap: Record<string, ProviderViewCallParticipant>;
  /**
   * Map of realtime call participants, keyed by participant ID.
   */
  realtimeCallParticipantsMap: Record<
    string,
    ProviderViewRealtimeCallParticipant
  >;
  /**
   * Map of sip call participants, keyed by participant ID.
   */
  sipCallParticipantsMap: Record<string, ProviderViewSipCallParticipant>;
  /**
   * A list of active participants in the current call.
   * Excludes participants who have already left the call.
   */
  callParticipants: ProviderViewCallParticipant[];
  /**
   * A list of all participants who were ever part of the current call,
   * including those who have already left.
   */
  allCallParticipants: ProviderViewCallParticipant[];
  /**
   * A list of all realtime call participants, including those who have already left. (Does not include sip call participants)
   */
  allRealtimeCallParticipants: ProviderViewRealtimeCallParticipant[];
  /**
   * The participant currently sharing their screen, if any.
   */
  screenSharingParticipant?: ProviderViewCallParticipant;
  /**
   * A list of realtime call participants.
   */
  realtimeCallParticipants: ProviderViewRealtimeCallParticipant[];
  /**
   * A list of sip call participants. (Does not exists in realtime)
   */
  sipCallParticipants: ProviderViewSipCallParticipant[];
};

export const useProviderViewCallParticipants =
  (): UseProviderViewCallParticipantsReturnType => {
    const sessionParticipantsMap = useAppSelector(getSessionParticipantsMap);
    const allSessionParticipantsMap = useAppSelector(
      getAllSessionParticipantsMap,
    );
    const currentRoomId = useAppSelector(getCurrentRoomId);

    const realtimeMembersMap = useAppSelector(getAllMembersMap);

    const callParticipantsMap = useMemo(
      () => getCallParticipantsMap(sessionParticipantsMap, realtimeMembersMap),
      [sessionParticipantsMap, realtimeMembersMap],
    );

    const { callParticipant: testCallParticipant } = useTestPatient();

    const callParticipantsMapWithTestPatient = useMemo(() => {
      if (!testCallParticipant) return callParticipantsMap;
      return {
        ...callParticipantsMap,
        [testCallParticipant.id]: testCallParticipant,
      };
    }, [callParticipantsMap, testCallParticipant]);

    const callParticipants = useMemo(
      () => Object.values(callParticipantsMapWithTestPatient),
      [callParticipantsMapWithTestPatient],
    );

    const allCallParticipants = useMemo(() => {
      const map = getCallParticipantsMap(
        allSessionParticipantsMap,
        realtimeMembersMap,
        currentRoomId,
      );
      if (testCallParticipant) {
        map[testCallParticipant.id] = testCallParticipant;
      }
      return Object.values(map);
    }, [
      allSessionParticipantsMap,
      realtimeMembersMap,
      currentRoomId,
      testCallParticipant,
    ]);

    const realtimeCallParticipants = useMemo(
      () =>
        callParticipants.filter(
          (
            participant: ProviderViewCallParticipant,
          ): participant is ProviderViewRealtimeCallParticipant =>
            !participant.isSipParticipant,
        ),
      [callParticipants],
    );

    const sipCallParticipants = useMemo(
      () =>
        callParticipants.filter(
          (
            participant: ProviderViewCallParticipant,
          ): participant is ProviderViewSipCallParticipant =>
            !!participant.isSipParticipant,
        ),
      [callParticipants],
    );

    const allRealtimeCallParticipants = useMemo(
      () =>
        allCallParticipants.filter(
          (
            participant: ProviderViewCallParticipant,
          ): participant is ProviderViewRealtimeCallParticipant =>
            !participant.isSipParticipant,
        ),
      [allCallParticipants],
    );

    const realtimeCallParticipantsMap = useMemo(
      () =>
        Object.fromEntries(
          realtimeCallParticipants.map(
            (participant): [string, ProviderViewRealtimeCallParticipant] => [
              participant.id,
              participant,
            ],
          ),
        ),
      [realtimeCallParticipants],
    );

    const sipCallParticipantsMap = useMemo(
      () =>
        Object.fromEntries(
          sipCallParticipants.map(
            (participant): [string, ProviderViewSipCallParticipant] => [
              participant.id,
              participant,
            ],
          ),
        ),
      [sipCallParticipants],
    );

    const screenSharingParticipant = useMemo(
      () => callParticipants.find((participant) => participant.screenElement),
      [callParticipants],
    );

    return {
      callParticipantsMap: callParticipantsMapWithTestPatient,
      realtimeCallParticipantsMap,
      sipCallParticipantsMap,
      callParticipants,
      allRealtimeCallParticipants,
      realtimeCallParticipants,
      sipCallParticipants,
      allCallParticipants,
      screenSharingParticipant,
    };
  };

const getCallParticipantsMap = (
  participantsMap: Record<string, SessionParticipant>,
  membersMap: Record<string, RealtimeMember>,
  currentRoomId?: string,
): Record<string, ProviderViewCallParticipant> =>
  Object.fromEntries(
    Object.entries(participantsMap).map(([id, participant]) => {
      const member = membersMap[id];
      if (participant.isSipParticipant) {
        return [
          id,
          {
            ...participant,
            identity: { id, role: RealtimeRole.Patient },
            picture: undefined,
            roomId: currentRoomId,
            isTestPatient: false,
          },
        ];
      } else {
        return [
          id,
          {
            ...member,
            ...participant,
            username: member?.state?.username,
            chatUserId: getChatUserIdFromIdentity(member?.identity),
          } as ProviderViewRealtimeCallParticipant,
        ];
      }
    }),
  );
